import React, { useRef, useEffect, useState } from "react";
import * as d3 from "d3";
import styled from "styled-components";
import { useHorizontalScroll } from "../../../utils/HorizontalScrollHook";
import { IInjuryChart } from "../../../types/IInjuryData";
import { useTheme } from "../../../contexts/ThemeContext";
import LabelNumber from "../../../styled-components/LabelNumber";
import noImageClub from "../../../assets/icons/noImageClub.svg"

interface InjuryBarChartProps {
  data: IInjuryChart[] | undefined;
}


const ChartContainer = styled.div`
  position: relative;
  min-width: 80%;
  display: flex;
  border: 2px solid ${({ theme }) => theme.playerDataBorder};
  min-height: 220px;
  border-radius: 10px;
  background-color: transparent;
  font-family: "DM Mono", monospace;
  margin-top: 20px;
  margin-bottom: 40px;
`;

const SideBar = styled.div`
  max-width: 250px;
  min-width: 250px;
  z-index: 2;
  font-size: large;
  min-height: 100%;
  border-right: 1px solid ${({ theme }) => theme.playerDataBorder};
  box-shadow: 10px -1px 10px -5px rgba(0, 0, 0, 0.25);
`;

const SidebarLower = styled.div`
  width: 90%;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  align-items: end;
  margin-left: 12px;
  padding-top: 24px;
  padding-bottom: 24px;
  > span:nth-child(2) {
    padding-top: 16px;
  }
  > span:last-child {
    padding-top: 24px;
  }
`;

const SVGContainer = styled.div`
  right: 0;
  overflow-x: scroll;
  overflow-x: scroll;
  z-index: 1;
  width: 100%;
  position: relative;
  overflow-y: hidden;
  
`;

const StyledUL = styled.ul`
  list-style: none;
  margin-block-end: 0px;
  padding-inline-start: 0px;
  > li {
    padding: 32px 20px 4px;
    text-align: right;
    border-bottom: 1px solid ${({ theme }) => theme.playerDataBorder};
    margin-left: 32px;
  }
  > div > li {
    padding: 32px 20px 4px;
    text-align: right;
    border-bottom: 1px solid ${({ theme }) => theme.playerDataBorder};
    margin-left: 32px;
  }
  > li:nth-child(1) {
    color: #7c7d79;
    font-size: 13px;
    font-weight: 500;
  }
  > div > li {
    font-weight: 900;
    color: ${({ theme }) => theme.color};
  }
`;

const YAxisContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const YAxis = styled.div`
  width: 80px; /* Adjust width as needed to match y-axis width */
  text-align: right;
`;

const YAxisTitle = styled.div`
  text-align: center;
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 10px;
`;

const InjuryBarChart: React.FC<InjuryBarChartProps> = ({ data = [] }) => {
  const { theme } = useTheme();
  const scrollRef = useHorizontalScroll();
  const barWidth = data.length > 10 ? 70 : scrollRef.current?.clientWidth! / data.length ;
  // const svgRef = useRef(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  // const [labels, setLabels] = useState<string[]>([]);
  const totalBarGroupWidth = data !== null ? data.length * barWidth : barWidth;
  const adjustedChartWidth = Math.max(scrollRef.current?.clientWidth!, totalBarGroupWidth);

  function getInjuryWeekLabels(injuryWeeks: number[], numLabels: number) {
    if (injuryWeeks.length > 0) {
      const maxWeek = Math.max(...injuryWeeks);
      const idealIntervalWidth = (maxWeek + 1) / (numLabels - 1);
      const labels = [];
      for (let i = 0; i < numLabels; i++) {
        let value: number;
        if (i === 0) {
          value = 0;
        } else if (i === numLabels - 1) {
          value = maxWeek;
        } else {
          value = Math.round(idealIntervalWidth * i);
        }
        labels.push(value.toString().padStart(2, "0"));
      }
      return labels;
    }
  }

  // useEffect(() => {
  //   const injuryWeeks = data?.map((d) => d.injuredWeek) ?? [0, 1, 2, 3, 4, 5];
  //   const calculatedLabels = getInjuryWeekLabels(injuryWeeks as number[], 5);
  //   setLabels(calculatedLabels);
  // }, [data]);

  const injuryWeeks = data?.map((d) => d.injuredWeek) ?? [0, 1, 2, 3, 4, 5];
  const labels = injuryWeeks && getInjuryWeekLabels(injuryWeeks as number[], 5);

  useEffect(() => {
    if (data !== null) {
      const labelHeight = (
        document.getElementsByClassName("side-label-container")[0] as HTMLElement
      ).getBoundingClientRect().height;
      const svg = d3.select(svgRef.current);
      svg.selectAll("*").remove();

      const barGroup = svg
        .append("g")
        .attr("height", labelHeight)
        .attr("width", adjustedChartWidth);

      //const colors = ["#F9FAF7", "#F2F5EE"];
      //const colors = ["#1B1B1B", "#141414"];
      const colors = [theme.playerContentBackground, theme.playerCardBackground];

      const tooltip = d3.select("body").append("div")
      .style("position", "fixed")
      .style("z-index", 999)
      .style("visibility", "hidden")
      .style("background-color", theme.secondaryButtonBackground)
      .style("color", theme.color)
      .style("border", "1px solid ${theme.secondaryButtonBackground}")
      .style("padding", "8px")
      .style("border-radius", "4px")
      .style("box-shadow", "0px 0px 6px rgba(0, 0, 0, 0.1)");

      if (data.length > 20) {
        data.forEach((_, i) => {
          barGroup
            .append("rect")
            .attr("width", barWidth)
            .classed("bar", true)
            .attr("height", labelHeight)
            .attr("x", i * barWidth)
            .attr("y", 0)
            .style("fill", colors[i % 2]);
        });
      } else {
        const numBars = adjustedChartWidth / barWidth;
        for (let i = 0; i < numBars; i++) {
          barGroup
            .append("rect")
            .attr("width", barWidth)
            .classed("bar", true)
            .attr("height", labelHeight)
            .attr("x", i * barWidth)
            .attr("y", 0)
            .style("fill", colors[i % 2]);
        }
      }

      data?.forEach((item, i) => {
        svg
          .append("text")
          .text(item.season)
          .style("fill", theme.NumberColor)
          .style("font-family", "DM Mono")
          .style("font-size", "14px")
          .style("font-weight", 500)
          .attr("x", i * barWidth + barWidth / 2 - 18)
          .attr("y", 343); // Position of the season label
        svg
          .append("text")
          .text(item.age)
          .style("fill", theme.NumberColor)
          .style("font-family", "DM Mono")
          .style("font-size", "14px")
          .style("font-weight", 500)
          .attr("x", i * barWidth + barWidth / 2 - 10)
          .attr("y", 371); // Adjust this y position to be under the season label

        // Append club logos
        // if (item.clubLogoURL) {
          svg
          .append("image")
          .attr("href", String(item.clubLogoURL ? item.clubLogoURL : noImageClub))
          .attr("width", 32)
          .attr("height", 32)
          .attr("x", i * barWidth + barWidth / 2 - 16)
          .attr("y", 390) // Adjust this y position
          .on("mouseover", (event, d) => {
            tooltip.style("visibility", "visible")
                .text(`${item.clubName}`);
        })
        .on("mousemove", (event) => {
            tooltip
            .style('left', `${event.clientX - 50}px`)
            .style('top', `${event.clientY - 50}px`);
        })
        .on("mouseout", () => {
            tooltip.style("visibility", "hidden");
        });
        // }
      });

      for (let i = 0; i < 6; i++) {
        barGroup
          .append("line")
          .attr("x1", 0)
          .attr("x2", adjustedChartWidth)
          .attr("y1", (labelHeight / 5) * i)
          .attr("y2", (labelHeight / 5) * i)
          .attr("stroke-width", 1)
          .attr("stroke", theme.playerDataBorder);
      }

      const xScale = d3
        .scaleLinear()
        .domain([0, Math.max(...data.map((d) => d.injuredWeek))])
        .range([0, (labelHeight / 5) * 4]);

      data?.forEach((item, i) => {
        barGroup
          .append("rect")
          .attr("width", 50)
          .attr("height", xScale(item.injuredWeek))
          .attr("x", i * barWidth + ((barWidth / 2) - 25))
          .attr("y", labelHeight - xScale(item.injuredWeek))
          .style("fill", "#FFC2C2")
          .attr("rx", 5);

        barGroup
          .append("rect")
          .attr("width", 50)
          .attr("height", xScale(item.injuredWeek) - 10)
          .attr("x", i * barWidth + ((barWidth / 2) - 25))
          .attr("y", labelHeight - xScale(item.injuredWeek) + 10)
          .style("fill", "#FFC2C2");
      });
    }
  }, [data, theme, adjustedChartWidth]);

  return (
    <ChartContainer>
      <SideBar>
        <StyledUL>
          <li>TIME INJURED (WKS)</li>
          <div className="side-label-container">
            <>
              {labels !== undefined && labels.reverse().map((label) => (
                <li className="side-label" key={label}>
                  {label}
                </li>
              ))}
            </>
          </div>
        </StyledUL>
        <SidebarLower>
          <LabelNumber className="attack-defense-data">SEASON</LabelNumber>
          <LabelNumber className="attack-defense-data">AGE AT START</LabelNumber>
          <LabelNumber className="attack-defense-data">CLUB</LabelNumber>
        </SidebarLower>
      </SideBar>
      {/* @ts-ignore */}
      <SVGContainer ref={scrollRef} id="injuryContainer" className="injury-chart-container">
        <svg
          ref={svgRef}
          style={{
            position: "absolute",
            top: "78px", //78
            height: "425px",
            width: adjustedChartWidth, // Use dynamic width
          }}
        ></svg>
      </SVGContainer>
    </ChartContainer>
  );
};

export default InjuryBarChart;

